import React from "react";
import { createRoot } from "react-dom/client";
import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import App from "./App";
import { msalConfig } from "./authConfig";
import { registerMsalInstance } from "./lib/authedFetch";

// MSAL v3 (msal-browser ≥3.x, msal-react ≥2.x) requires an explicit async
// initialize() before ANY public API — loginRedirect, acquireTokenSilent,
// handleRedirectPromise. Calling them on an uninitialized instance throws
// `uninitialized_public_client_application`, which fires-and-forgets on
// the Sign-In button's onClick (no .catch) and reads as "nothing happens."
//
// Also resolves any in-flight redirect response (returning from AAD) and
// hydrates the active account from the cache, so a refresh on an already-
// signed-in tab lands on AuthenticatedTemplate immediately.
const msalInstance = new PublicClientApplication(msalConfig);

async function bootstrap() {
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();

  // Hand the initialized instance to the app's single authenticated fetch
  // (src/lib/authedFetch.ts) so every request reads a fresh bearer from MSAL's
  // cache at send time — no React state involved, no stale copies.
  registerMsalInstance(msalInstance);

  // Pick a default active account if none is set but cache has accounts.
  if (!msalInstance.getActiveAccount()) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  // Keep the active account in sync as login/logout events fire.
  msalInstance.addEventCallback((e) => {
    if (e.eventType === EventType.LOGIN_SUCCESS) {
      const acct = (e.payload as { account?: import("@azure/msal-browser").AccountInfo } | null)?.account;
      if (acct) msalInstance.setActiveAccount(acct);
    }
  });

  const container = document.getElementById("root");
  if (!container) throw new Error("No #root container in index.html");
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </React.StrictMode>,
  );
}

bootstrap().catch((err) => {
  // Surface a top-level boot failure so a broken MSAL config doesn't read
  // as a silent white screen.
  // eslint-disable-next-line no-console
  console.error("[bootstrap] failed to initialize app", err);
  const container = document.getElementById("root");
  if (container) {
    container.innerHTML =
      `<div style="font-family:system-ui;padding:24px;max-width:640px;margin:48px auto;color:#1a1a1a">
        <h2>App failed to start</h2>
        <p>The authentication client failed to initialize. Check the browser console for details.</p>
        <pre style="white-space:pre-wrap;background:#f5f5f5;padding:12px;border-radius:6px;font-size:12px">${
          (err as Error)?.message || String(err)
        }</pre>
      </div>`;
  }
});
